import { makeAutoObservable } from "mobx";
import { createContext, useContext } from "react";
import { assert, orderUtilsMutable } from "utils";

import {
  type WorkoutEditStore,
  type WorkoutItemLayout,
} from "src/workouts/WorkoutEditStore";

import { canDropOnTarget, type MinimapInstruction } from "./minimapUtils";

function collectAllGroupIds(items: WorkoutItemLayout[]): string[] {
  const ids: string[] = [];

  for (const item of items) {
    if (item.type === "GROUP") {
      ids.push(item.workoutItemId);

      if (item.children) {
        ids.push(...collectAllGroupIds(item.children));
      }
    }
  }

  return ids;
}

const MINIMAP_DRAG_AND_DROP_MANAGER_ID = Symbol.for(
  "MINIMAP_DRAG_AND_DROP_MANAGER_ID",
);

interface ActiveDropTarget {
  id: string;
  instruction: MinimapInstruction;
}

export class MinimapDragAndDropManager {
  workoutEditStore: WorkoutEditStore;

  draggedItemId: string | null = null;
  isDragging: boolean = false;
  activeDropTarget: ActiveDropTarget | null = null;

  expandedItems: string[] = [];

  constructor(workoutEditStore: WorkoutEditStore) {
    this.workoutEditStore = workoutEditStore;
    this.expandedItems = collectAllGroupIds(workoutEditStore.layout.items);
    makeAutoObservable(this, {
      workoutEditStore: false,
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedItems.includes(id);
  }

  toggleExpanded(id: string) {
    if (this.expandedItems.includes(id)) {
      this.expandedItems = this.expandedItems.filter((item) => {
        return item !== id;
      });
    } else {
      this.expandedItems.push(id);
    }
  }

  expandItem(id: string) {
    if (!this.expandedItems.includes(id)) {
      this.expandedItems.push(id);
    }
  }

  get allExpanded(): boolean {
    const allGroupIds = collectAllGroupIds(this.workoutEditStore.layout.items);
    return (
      allGroupIds.length > 0 &&
      allGroupIds.every((id) => {
        return this.expandedItems.includes(id);
      })
    );
  }

  expandAll() {
    this.expandedItems = collectAllGroupIds(this.workoutEditStore.layout.items);
  }

  collapseAll() {
    this.expandedItems = [];
  }

  syncExpandedItems() {
    const allGroupIds = collectAllGroupIds(this.workoutEditStore.layout.items);

    for (const groupId of allGroupIds) {
      if (!this.expandedItems.includes(groupId)) {
        this.expandedItems.push(groupId);
      }
    }
  }

  get id() {
    return MINIMAP_DRAG_AND_DROP_MANAGER_ID;
  }

  get horizontalIndent() {
    return 16;
  }

  startDrag(itemId: string) {
    this.draggedItemId = itemId;
    this.isDragging = true;
  }

  endDrag() {
    this.draggedItemId = null;
    this.isDragging = false;
    this.activeDropTarget = null;
  }

  setActiveDropTarget(id: string, instruction: MinimapInstruction) {
    this.activeDropTarget = { id, instruction };
  }

  clearActiveDropTarget() {
    this.activeDropTarget = null;
  }

  getInstructionForTarget(targetId: string): MinimapInstruction | null {
    if (this.activeDropTarget?.id === targetId) {
      return this.activeDropTarget.instruction;
    }

    return null;
  }

  canDropOnTarget(
    sourceId: string,
    targetId: string,
    instruction: MinimapInstruction,
  ): boolean {
    return canDropOnTarget(
      sourceId,
      targetId,
      instruction,
      this.workoutEditStore,
    ).allowed;
  }

  applyDrop(
    sourceId: string,
    targetId: string,
    instruction: MinimapInstruction,
  ) {
    const source = this.workoutEditStore.getWorkoutItemById(sourceId);
    const target = this.workoutEditStore.getWorkoutItemById(targetId);

    if (!source || !target) {
      return;
    }

    const sourceGroup =
      source.parentId === null
        ? this.workoutEditStore.rootWorkoutItems
        : (this.workoutEditStore.workoutItemsByParentId[source.parentId] ?? []);

    let destGroup: typeof sourceGroup;

    if (instruction === "make-child") {
      destGroup = this.workoutEditStore.workoutItemsByParentId[target.id] ?? [];
    } else if (target.parentId === null) {
      destGroup = this.workoutEditStore.rootWorkoutItems;
    } else {
      destGroup =
        this.workoutEditStore.workoutItemsByParentId[target.parentId] ?? [];
    }

    orderUtilsMutable.moveByInstruction(
      source,
      target,
      sourceGroup,
      destGroup,
      instruction,
      "parentId",
    );
  }
}

export const minimapDragAndDropManagerContext =
  createContext<MinimapDragAndDropManager | null>(null);

export function useMinimapDragAndDropManager(): MinimapDragAndDropManager {
  const context = useContext(minimapDragAndDropManagerContext);
  assert(
    context !== null,
    "useMinimapDragAndDropManager must be used within a MinimapDragAndDropManagerProvider",
  );
  return context;
}
